import { deriveDarkColors, isHexColor } from '../../helpers/brandColors';

// Shared vocabulary of the Brand Identity page: the color roles of the brand kit, how to word
// where a value came from, and the patch a save sends.

export const COLOR_ROLES = [
  { key: 'primary', label: 'Primary', hint: 'Buttons, links and accents' },
  { key: 'text', label: 'Text', hint: 'Headings and body text' },
  { key: 'textMuted', label: 'Muted text', hint: 'Small print' },
  { key: 'page', label: 'Page', hint: 'Behind the email column' },
  { key: 'content', label: 'Content', hint: 'The email column and cards' },
  { key: 'soft', label: 'Soft', hint: 'Tinted panels' },
  { key: 'highlight', label: 'Highlight', hint: 'The one loud accent' },
  { key: 'dark', label: 'Dark', hint: 'Dark panels and heroes' },
];

export const DARK_ROLES = [
  { key: 'page', label: 'Page', hint: 'Behind the email column' },
  { key: 'content', label: 'Content', hint: 'The email column and cards' },
  { key: 'text', label: 'Text', hint: 'Headings and body text' },
  { key: 'textMuted', label: 'Muted text', hint: 'Small print' },
  { key: 'link', label: 'Links and buttons', hint: 'Buttons, links and accents' },
  { key: 'soft', label: 'Soft', hint: 'Tinted panels' },
  { key: 'dark', label: 'Dark', hint: 'Dark panels and the footer' },
];

// What the preview falls back to for a role nobody has filled in yet. Never saved.
export const PREVIEW_DEFAULTS = {
  primary: '#3B5BDB',
  text: '#222222',
  textMuted: '#6B6B6B',
  page: '#F4F4F4',
  content: '#FFFFFF',
  soft: '#EEF1FB',
  highlight: '#FFD43B',
  dark: '#1E1E2A',
};

// What the preview and the kit's placeholders show: the draft's valid colors over the domain's
// primary color over PREVIEW_DEFAULTS, the dark palette derived from that, and the dark palette
// the preview draws (the draft's dark colors where set, the derived ones elsewhere).
export const previewPalette = (visual = {}, domainPrimary) => {
  const colors = { ...PREVIEW_DEFAULTS, ...(isHexColor(domainPrimary) ? { primary: domainPrimary } : {}) };
  Object.entries(visual.colors || {}).forEach(([key, value]) => {
    if (isHexColor(value)) {
      colors[key] = value;
    }
  });
  const derivedDark = deriveDarkColors(colors);
  const dark = Object.fromEntries(
    DARK_ROLES.map(({ key }) => [key, isHexColor(visual.darkColors?.[key]) ? visual.darkColors[key] : derivedDark[key]])
  );
  return { colors, derivedDark, dark };
};

// Color roles that are filled but not #RRGGBB, keyed like the API's 400 ("colors.primary").
export const invalidColorFields = (visual) => {
  const errors = {};
  ['colors', 'darkColors'].forEach((group) =>
    Object.entries(visual?.[group] || {}).forEach(([key, value]) => {
      if (value && !isHexColor(value)) {
        errors[`${group}.${key}`] = 'Use a hex color like #1A2B3C';
      }
    })
  );
  return errors;
};

export const SOURCE_LABELS = {
  cssVariable: 'CSS variable',
  meta: 'meta tag',
  computed: 'styles',
  screenshot: 'screenshot',
  header: 'header',
  jsonLd: 'structured data',
  icon: 'icon',
  derived: 'worked out from your colors',
};

export const FAILURE_REASONS = {
  timeout: 'your website took too long to load',
  blocked: 'it blocked our bot',
  emptyPage: 'the page came back empty',
  model: 'we could not make sense of what we found',
};

export const failureMessage = (reason) =>
  FAILURE_REASONS[reason]
    ? `We couldn't read your website: ${FAILURE_REASONS[reason]}. Add your logo and colors below.`
    : "We couldn't read your website. Add your logo and colors below.";

// A failed re-scan of a brand that already has its colors: nothing is lost, so no call to fill it in.
export const rescanFailureMessage = (reason) =>
  FAILURE_REASONS[reason]
    ? `We couldn't read your website again: ${FAILURE_REASONS[reason]}. Your saved brand is unchanged.`
    : "We couldn't read your website again. Your saved brand is unchanged.";

// The API's 403 rule for a full run: an approved brand only gets its look re-read, into a proposal,
// unless it was skipped afterwards.
export const isVisualOnlyRun = (brandContext) => !!brandContext?.approvedAt && brandContext.status !== 'skipped';

// Which visualSources keys a visual edit touches, so their "From your site" label goes away.
export const touchedSourceKeys = (before = {}, change = {}) =>
  Object.keys(change).flatMap((key) => {
    if (key === 'colors') {
      return Object.keys({ ...before.colors, ...change.colors })
        .filter((role) => (before.colors?.[role] || '') !== (change.colors?.[role] || ''))
        .map((role) => `colors.${role}`);
    }
    return same(before[key], change[key]) ? [] : [key];
  });

export const VOICE_FIELDS = ['description', 'archetypes', 'audiences', 'tones', 'voices'];

// A run counts as still going for any of these; anything else (finished, failed, absent) is done.
const IN_PROGRESS = ['generating', 'pending', 'queued', 'running'];
export const isRunning = (status) => IN_PROGRESS.includes(status);

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// Drops empty color roles so a half-filled kit is not sent as empty strings.
const cleanColors = (colors) => {
  if (!colors) {
    return colors;
  }
  const entries = Object.entries(colors).filter(([, value]) => value);
  return entries.length ? Object.fromEntries(entries) : undefined;
};

export const cleanVisual = (visual) => {
  if (!visual) {
    return visual;
  }
  const next = { ...visual, colors: cleanColors(visual.colors), darkColors: cleanColors(visual.darkColors) };
  Object.keys(next).forEach((key) => {
    if (next[key] === undefined || next[key] === '') {
      delete next[key];
    }
  });
  return next;
};

// Only what the client changed: each edited voice field, and the visual as a whole when any part
// of it moved. The saved visual is the base, so footer, social and logoWidth (edited in the design
// studio, not here) ride along untouched.
export const brandContextPatch = (saved, draft) => {
  // No draft at all means nothing has been edited: the form renders once with a brand a run just
  // wrote into the store, before the effect copies it into the draft.
  if (!draft) {
    return {};
  }
  const patch = {};
  VOICE_FIELDS.forEach((field) => {
    if (!same(saved?.[field], draft?.[field])) {
      patch[field] = draft[field];
    }
  });
  const visual = cleanVisual({ ...(saved?.visual || {}), ...(draft?.visual || {}) });
  if (!same(cleanVisual(saved?.visual || {}), visual)) {
    patch.visual = visual;
  }
  return patch;
};

// A 400 like "visual.colors.primary must be #RRGGBB" belongs on that field: returns the path
// after "visual." ("colors.primary"), or null when the message names no field.
export const fieldOfError = (message) => {
  const match = /^visual\.([\w.]+)/.exec(message || '');
  return match ? match[1] : null;
};

const FONT_HOSTS = ['fonts.googleapis.com', 'use.typekit.net', 'fonts.bunny.net'];

// The preview hoists this link into the admin document, and the URL can come from a scraped website.
export const safeWebFontUrl = (url) => {
  try {
    const { protocol, hostname } = new URL(url);
    return protocol === 'https:' && FONT_HOSTS.includes(hostname) ? url : null;
  } catch {
    return null;
  }
};

// The same answer the domain GET gives as brandIdentityStatus, worked out from a row the page
// just saved, so the Setup Guide moves without a reload.
export const brandIdentityStatusOf = (brandContext) => {
  if (!brandContext || brandContext.status === 'skipped') {
    return 'missing';
  }
  if (!brandContext.approvedAt) {
    return 'proposed';
  }
  return brandContext.visual?.logoDarkUrl ? 'confirmed' : 'needsDarkLogo';
};
